import * as React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import usePqTransform from 'hooks/usePqTransform';
import useReportSummary, {
  ReportDetailRow,
  ReportSummaryResponse,
} from 'hooks/useReportSummary';
import { useTranslation } from 'react-i18next';

interface ReportProps {
  reportId: string;
  productId: number;
  version: string;
}

const ProductSummaryTable: React.FC<ReportProps> = (props) => {
  const { reportId, productId, version } = props;

  const { t } = useTranslation();
  const pq = usePqTransform();

  const { loading, error, data } = useReportSummary(reportId);

  const getProductRow = (
    summary: ReportSummaryResponse,
    productId: number,
    version: string,
  ): ReportDetailRow | undefined => {
    return summary.allReportJobs.nodes
      .flatMap((n) => n.reportSummariesByReportId)
      .flatMap((e) => e.edges)
      .map((n) => n.node)
      .find(
        (r) => r.productByProductId.id === productId && r.version === version,
      );
  };

  if (loading || error) {
    return <div />;
  }

  const productRow = getProductRow(data, productId, version);
  if (!productRow) {
    return <div />;
  }

  return (
    <div key={`reportProductSummary-${productId}`}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="right">
              <Tooltip
                title={
                  <div>
                    <em>{t('reports.prodSummary.pq')}</em>
                    {t('explainers.pqContent')}
                  </div>
                }
                arrow
              >
                <div>{t('reports.prodSummary.pq')}</div>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <Tooltip
                title={
                  <div>
                    <em>{t('reports.prodSummary.winRate')}</em>
                    {t('explainers.winRateContent')}
                  </div>
                }
                arrow
              >
                <div>{t('reports.prodSummary.winRate')}</div>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <Tooltip
                title={
                  <div>
                    <em>{t('reports.summary.polarization')}</em>
                    {t('explainers.polarizationContent')}
                  </div>
                }
                arrow
              >
                <div>{t('reports.summary.polarization')}</div>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell align="right">
              {pq.scale === '9pt' ? (
                <Tooltip
                  title={
                    <div style={{ fontFamily: 'monospace', fontSize: '12px', lineHeight: 1.8 }}>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>Scale conversion</div>
                      <div>Map [a, b] to [c, d]:</div>
                      <div>y = (x - a) / (b - a) x (d - c) + c</div>
                      <div style={{ marginTop: '4px' }}>Applied: a = 1, b = 7, c = 1, d = 9</div>
                      <div style={{ fontWeight: 600 }}>PQ9 = (PQ7 - 1) / (7 - 1) x (9 - 1) + 1</div>
                    </div>
                  }
                  arrow
                >
                  <div style={{ cursor: 'help' }}>{pq.format(productRow.pq)}</div>
                </Tooltip>
              ) : (
                pq.format(productRow.pq)
              )}
            </TableCell>
            <TableCell align="right">
              {parseFloat(productRow.pComp).toFixed(0)}%
            </TableCell>
            <TableCell align="right">
              {parseFloat(productRow.polarization).toFixed(2)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

export default ProductSummaryTable;
